import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Eye, EyeOff, KeyRound, CheckCircle2, AlertTriangle, Loader2, ArrowLeft } from "lucide-react";
import { API } from "../utils/api";

export default function ResetPasswordPage() {
  const [searchParams]   = useSearchParams();
  const navigate         = useNavigate();
  const token            = searchParams.get("token") || "";

  const [newPass,    setNewPass]    = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showNew,    setShowNew]    = useState(false);
  const [showConf,   setShowConf]   = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [success,    setSuccess]    = useState(false);
  const [error,      setError]      = useState("");

  // If no token in URL, show invalid state immediately
  useEffect(() => {
    if (!token) setError("El enlace de recuperación es inválido o está incompleto.");
  }, [token]);

  const strength = (() => {
    if (newPass.length === 0) return 0;
    let s = 0;
    if (newPass.length >= 8) s++;
    if (/[A-Z]/.test(newPass)) s++;
    if (/[0-9]/.test(newPass)) s++;
    if (/[^A-Za-z0-9]/.test(newPass)) s++;
    return s;
  })();

  const strengthLabel = ["", "Débil", "Regular", "Buena", "Fuerte"][strength];
  const strengthColor = ["", "#ef4444", "#f59e0b", "#3b82f6", "#22c55e"][strength];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPass.length < 8) { setError("La contraseña debe tener al menos 8 caracteres."); return; }
    if (newPass !== confirmPass) { setError("Las contraseñas no coinciden."); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ token, new_password: newPass }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(d.detail || "Error al actualizar la contraseña.");
        return;
      }
      setSuccess(true);
      // Auto-redirect to login after 3 s
      setTimeout(() => navigate("/login", { replace: true }), 3000);
    } catch {
      setError("Error de conexión. Verificá tu internet.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-6 overflow-hidden">
      {/* Video fondo */}
      <video autoPlay muted loop playsInline
        className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none"
        style={{ opacity: 0.35 }}
      >
        <source
          src="https://customer-assets.emergentagent.com/job_5c81eca5-82cd-4071-a85b-2346e52a1b97/artifacts/jh39v1rk_Video_de_Fa%CC%81brica_de_Pa%CC%81ginas_Web%20%281%29.mp4"
          type="video/mp4"
        />
      </video>
      <div className="absolute inset-0 bg-black/80 z-0 pointer-events-none" />

      <div className="relative z-10 w-full max-w-sm animate-fadeInUp">

        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img src="/logo.png" alt="Sitios YA" className="h-32 w-auto object-contain mb-5 drop-shadow-2xl" />
          <p className="text-sm text-gray-400 tracking-wide">Nueva contraseña</p>
        </div>

        {/* Card */}
        <div className="bg-[#161616] border border-white/10 rounded-3xl p-6 shadow-2xl">

          {success ? (
            /* ── Success ── */
            <div className="flex flex-col items-center text-center gap-4 py-4" data-testid="reset-password-success">
              <div className="w-16 h-16 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center">
                <CheckCircle2 size={32} className="text-green-400" />
              </div>
              <div>
                <p className="text-white font-black text-lg mb-2">¡Contraseña actualizada!</p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Tu contraseña fue cambiada exitosamente.<br />
                  Redirigiendo al inicio de sesión...
                </p>
              </div>
              <Link to="/login" className="mt-1 flex items-center gap-2 text-sm text-[#ffc700] font-bold hover:underline">
                Ir al login ahora
              </Link>
            </div>

          ) : !token ? (
            /* ── Invalid token state ── */
            <div className="flex flex-col items-center text-center gap-4 py-4" data-testid="reset-password-invalid">
              <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
                <AlertTriangle size={32} className="text-red-400" />
              </div>
              <div>
                <p className="text-white font-black text-lg mb-2">Enlace inválido</p>
                <p className="text-gray-400 text-sm">Este enlace de recuperación es inválido o ya fue utilizado.</p>
              </div>
              <Link
                to="/forgot-password"
                className="mt-2 py-3 px-6 bg-[#ffc700] text-black rounded-2xl font-black text-sm hover:bg-[#e6b300] transition-colors"
              >
                Solicitar nuevo enlace
              </Link>
            </div>

          ) : (
            /* ── Form ── */
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="mb-1">
                <p className="text-white font-black text-lg mb-1">Crear nueva contraseña</p>
                <p className="text-gray-400 text-sm">Elegí una contraseña segura para tu cuenta.</p>
              </div>

              {error && (
                <div
                  data-testid="reset-password-error"
                  className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded-xl flex items-start gap-2"
                >
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  {error}
                </div>
              )}

              {/* New password */}
              <div>
                <label className="text-xs font-semibold text-gray-400 block mb-1.5 uppercase tracking-wide">
                  Nueva contraseña
                </label>
                <div className="relative">
                  <input
                    data-testid="reset-new-password-input"
                    type={showNew ? "text" : "password"}
                    value={newPass}
                    onChange={(e) => setNewPass(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    required
                    autoComplete="new-password"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 pr-11 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#ffc700]/50 transition-colors"
                  />
                  <button type="button" onClick={() => setShowNew(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors">
                    {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                {/* Strength bar */}
                {newPass.length > 0 && (
                  <div className="mt-2">
                    <div className="flex gap-1 mb-1">
                      {[1,2,3,4].map(i => (
                        <div key={i} className="flex-1 h-1 rounded-full transition-all duration-300"
                          style={{ background: i <= strength ? strengthColor : "rgba(255,255,255,0.1)" }} />
                      ))}
                    </div>
                    <p className="text-xs font-semibold" style={{ color: strengthColor }}>{strengthLabel}</p>
                  </div>
                )}
              </div>

              {/* Confirm password */}
              <div>
                <label className="text-xs font-semibold text-gray-400 block mb-1.5 uppercase tracking-wide">
                  Confirmar contraseña
                </label>
                <div className="relative">
                  <input
                    data-testid="reset-confirm-password-input"
                    type={showConf ? "text" : "password"}
                    value={confirmPass}
                    onChange={(e) => setConfirmPass(e.target.value)}
                    placeholder="Repetí la contraseña"
                    required
                    autoComplete="new-password"
                    className={`w-full bg-white/5 border rounded-xl px-4 py-3 pr-11 text-sm text-white placeholder-gray-600 focus:outline-none transition-colors ${
                      confirmPass && confirmPass !== newPass
                        ? "border-red-500/50"
                        : confirmPass && confirmPass === newPass
                        ? "border-green-500/50"
                        : "border-white/10 focus:border-[#ffc700]/50"
                    }`}
                  />
                  <button type="button" onClick={() => setShowConf(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors">
                    {showConf ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {confirmPass && confirmPass !== newPass && (
                  <p className="text-xs text-red-400 mt-1">Las contraseñas no coinciden</p>
                )}
              </div>

              <button
                data-testid="reset-password-submit-btn"
                type="submit"
                disabled={loading || newPass.length < 8 || newPass !== confirmPass}
                className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#ffc700] text-black rounded-2xl font-black text-sm hover:bg-[#e6b300] transition-colors disabled:opacity-60 mt-1 tracking-wide"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
                {loading ? "Actualizando..." : "Actualizar contraseña"}
              </button>
            </form>
          )}
        </div>

        {!success && token && (
          <div className="flex justify-center mt-5">
            <Link to="/forgot-password"
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors">
              <ArrowLeft size={12} /> Solicitar nuevo enlace
            </Link>
          </div>
        )}

        <p className="text-center text-xs text-gray-600 mt-5">
          Sitios YA &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
